import { ensureValidToken, getUserInfo } from '../auth/keycloak';
import { API_CONFIG } from '../config/env';
import { logger } from '../utils/logger';
import { initFeTelemetry, recordFeApiTiming } from './feTelemetry';
import { getLastUiAction } from './feTelemetry/behavior';
import { shouldTrackApiPath } from './feTelemetry/routeContext';

const TELEMETRY_PATH = '/api/client-telemetry/errors';
const MAX_STRING_LENGTH = 4000;
const MAX_ARRAY_ITEMS = 50;
const MAX_OBJECT_KEYS = 60;
const MAX_DEPTH = 6;

type UiAction = {
  type: string;
  label?: string;
  selector?: string;
  path?: string;
  timestamp: string;
};

type ApiErrorTelemetryInput = {
  path: string;
  baseUrl?: string;
  method: string;
  requestInit?: RequestInit;
  requestBody?: unknown;
  status?: number;
  statusText?: string;
  responseBody?: unknown;
  responseHeaders?: Headers;
  durationMs?: number;
  requestUrl?: string;
  error?: unknown;
};

let fetchTelemetryInitialized = false;
let nativeFetch: typeof fetch | null = null;

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function lastUiActionSnapshot(): UiAction | null {
  return getLastUiAction();
}

function truncate(value: string, max = MAX_STRING_LENGTH): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}...(truncated)`;
}

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (depth >= MAX_DEPTH) return '[MaxDepthReached]';

  if (typeof value === 'string') return truncate(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    const entries = Object.entries(value as Record<string, unknown>).slice(0, MAX_OBJECT_KEYS);
    for (const [k, v] of entries) {
      result[k] = sanitizeValue(v, depth + 1);
    }
    return result;
  }

  return String(value);
}

function sanitizeHeaders(headersInit: HeadersInit | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const headers = new Headers(headersInit || {});
  headers.forEach((value, key) => {
    const lower = key.toLowerCase();
    if (lower === 'authorization' || lower === 'cookie' || lower === 'set-cookie') {
      return;
    }
    out[key] = truncate(value, 500);
  });
  return out;
}

function pickHeaderValue(headersInit: HeadersInit | undefined, names: string[]): string | null {
  if (!headersInit || names.length === 0) {
    return null;
  }

  const headers = new Headers(headersInit);
  for (const name of names) {
    const value = headers.get(name);
    if (value && value.trim()) {
      return truncate(value.trim(), 200);
    }
  }

  return null;
}

function sanitizeRequestBody(body: unknown): unknown {
  if (body == null) return body;

  if (typeof body === 'string') {
    try {
      return sanitizeValue(JSON.parse(body));
    } catch {
      return truncate(body);
    }
  }

  if (body instanceof URLSearchParams) {
    return truncate(body.toString());
  }

  if (body instanceof FormData) {
    const keys: string[] = [];
    body.forEach((_, key) => {
      if (keys.length < MAX_ARRAY_ITEMS) keys.push(key);
    });
    return { formDataKeys: keys };
  }

  return sanitizeValue(body);
}

async function sendTelemetry(payload: Record<string, unknown>) {
  try {
    const token = await ensureValidToken(10);
    if (!token) return;

    const fetchImpl = nativeFetch || (typeof window !== 'undefined' ? window.fetch.bind(window) : fetch);

    await fetchImpl(`${API_CONFIG.baseUrl}${TELEMETRY_PATH}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Telemetry-Source': 'frontend-api',
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    // Never block UX when telemetry fails.
    logger.debug('Client telemetry send failed', e);
  }
}

function getEnvSnapshot() {
  if (typeof window === 'undefined') return {};

  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    online: navigator.onLine,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
    },
    appMode: import.meta.env.MODE,
    appDev: import.meta.env.DEV,
    apiBaseUrl: API_CONFIG.baseUrl,
  };
}

export function reportApiErrorTelemetry(input: ApiErrorTelemetryInput) {
  if (input.path.startsWith('/api/client-telemetry')) {
    return;
  }

  const user = getUserInfo();

  const payload: Record<string, unknown> = {
    category: 'API_ERROR',
    occurredAt: new Date().toISOString(),
    page: typeof window === 'undefined'
      ? {}
      : {
          url: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          referrer: document.referrer,
        },
    user: user
      ? {
          id: user.id,
          username: user.username,
          email: user.email,
          name: user.name,
          roles: user.roles,
        }
      : null,
    uiAction: lastUiActionSnapshot(),
    request: {
      baseUrl: input.baseUrl || API_CONFIG.baseUrl,
      path: input.path,
      url: input.requestUrl,
      method: input.method,
      headers: sanitizeHeaders(input.requestInit?.headers),
      body: sanitizeRequestBody(input.requestBody ?? input.requestInit?.body),
      credentials: input.requestInit?.credentials,
      clientRequestId: pickHeaderValue(input.requestInit?.headers, ['x-client-request-id']),
      sentryTrace: pickHeaderValue(input.requestInit?.headers, ['sentry-trace']),
    },
    response: {
      status: input.status,
      statusText: input.statusText,
      requestId: input.responseHeaders?.get('X-Request-ID') || input.responseHeaders?.get('x-request-id') || null,
      traceId: input.responseHeaders?.get('X-Trace-Id') || input.responseHeaders?.get('x-trace-id') || null,
      body: sanitizeValue(input.responseBody),
    },
    timing: {
      durationMs: typeof input.durationMs === 'number' ? Math.max(0, Math.round(input.durationMs)) : null,
    },
    error: {
      message: input.error instanceof Error ? input.error.message : sanitizeValue(input.error),
      name: input.error instanceof Error ? input.error.name : undefined,
      stack: input.error instanceof Error ? truncate(input.error.stack || '') : undefined,
    },
    environment: getEnvSnapshot(),
  };

  void sendTelemetry(payload);
}

function toPathFromInput(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    try {
      const u = new URL(input, window.location.origin);
      return `${u.pathname}${u.search}`;
    } catch {
      return input;
    }
  }
  if (input instanceof URL) {
    return `${input.pathname}${input.search}`;
  }
  const request = input as Request;
  try {
    const u = new URL(request.url, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return request.url;
  }
}

function isAssetPath(path: string): boolean {
  if (!path) return false;
  return /(^|\/)assets\//i.test(path) || /\.(js|css|map|png|jpe?g|gif|svg|webp|ico|woff2?|ttf)$/i.test(path);
}

function isKnownDeployNoise(params: { path: string; method: string; status?: number; error?: unknown }): boolean {
  const { path, method, status, error } = params;
  if (!isAssetPath(path)) return false;

  const msg =
    error instanceof Error
      ? `${error.name || ''} ${error.message || ''} ${error.stack || ''}`.toLowerCase()
      : String(error ?? '').toLowerCase();

  // Ignore transient asset/cache mismatch right after deploy (Failed to fetch, chunk load failures).
  if (msg.includes('failed to fetch') || msg.includes('chunkloaderror') || msg.includes('loading chunk')) {
    return true;
  }

  // Also ignore common CDN/static cache miss family for asset requests.
  if (status != null && [0, 404, 410].includes(status) && method === 'GET') {
    return true;
  }

  return false;
}

function shouldReportTelemetry(params: { method: string; status?: number; path: string; error?: unknown }): boolean {
  const method = params.method.toUpperCase();
  const status = params.status;

  if (isKnownDeployNoise({ path: params.path, method, status, error: params.error })) {
    return false;
  }

  if (status != null) {
    if (status >= 500) return true;
    return MUTATING_METHODS.has(method);
  }

  // Network/runtime error without HTTP status: prioritize write/update requests.
  return MUTATING_METHODS.has(method);
}

function reportApiTiming(params: {
  method: string;
  path: string;
  status?: number;
  durationMs: number;
  ok: boolean;
  responseHeaders?: Headers;
}) {
  recordFeApiTiming({
    method: params.method,
    path: params.path,
    status: params.status,
    durationMs: params.durationMs,
    ok: params.ok,
    requestId: params.responseHeaders?.get('X-Request-ID') || params.responseHeaders?.get('x-request-id'),
  });
}

export function initGlobalFetchErrorTelemetry() {
  if (fetchTelemetryInitialized || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  fetchTelemetryInitialized = true;
  nativeFetch = window.fetch.bind(window);
  initFeTelemetry(nativeFetch);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method || ((input as Request)?.method ?? 'GET')).toString().toUpperCase();
    const path = toPathFromInput(input);
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const requestUrl = (() => {
      if (typeof input === 'string') return input;
      if (input instanceof URL) return input.toString();
      return (input as Request).url;
    })();

    try {
      const response = await nativeFetch!(input, init);
      const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const durationMs = endedAt - startedAt;
      if (shouldTrackApiPath(path)) {
        reportApiTiming({
          method,
          path,
          status: response.status,
          durationMs,
          ok: response.ok,
          responseHeaders: response.headers,
        });
      }
      if (!response.ok && !path.startsWith('/api/client-telemetry')) {
        let responsePayload: unknown = null;
        try {
          const text = await response.clone().text();
          if (text) {
            try {
              responsePayload = JSON.parse(text);
            } catch {
              responsePayload = text;
            }
          }
        } catch {
          responsePayload = null;
        }

        if (shouldReportTelemetry({ method, status: response.status, path, error: new Error(`HTTP ${response.status}`) })) {
          reportApiErrorTelemetry({
            path,
            method,
            requestInit: init,
            status: response.status,
            statusText: response.statusText,
            responseBody: responsePayload,
            responseHeaders: response.headers,
            durationMs,
            requestUrl,
            error: new Error(`HTTP ${response.status}`),
          });
        }
      }
      return response;
    } catch (error) {
      const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const durationMs = endedAt - startedAt;
      if (shouldTrackApiPath(path)) {
        reportApiTiming({
          method,
          path,
          durationMs,
          ok: false,
        });
      }
      if (!path.startsWith('/api/client-telemetry')) {
        if (shouldReportTelemetry({ method, path, error })) {
          reportApiErrorTelemetry({
            path,
            method,
            requestInit: init,
            durationMs,
            requestUrl,
            error,
          });
        }
      }
      throw error;
    }
  };
}
